import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getMergePlan, mergeListings, undoListingMerge } from '../api';
import { eventPricingKeys } from './useEventPricing';

// Event ids come from the route as strings; keys always hold the string so a
// numeric id can never miss the cache entry
export const mergePlanKeys = {
  all: ['mergePlan'],
  detail: (eventId) => ['mergePlan', String(eventId)],
};

// The plan is built from the server's inventory cache (no Skybox call), so a
// short staleTime is cheap: opening the modal re-reads a plan older than this
export const MERGE_PLAN_STALE_MS = 15 * 1000;

// { groups, blocked, recent } for Compare's "Merge listings (N)" button and modal
export function useMergePlan(eventId, options = {}) {
  return useQuery({
    queryKey: mergePlanKeys.detail(eventId),
    queryFn: async () => (await getMergePlan(eventId)).data,
    enabled: !!eventId,
    staleTime: MERGE_PLAN_STALE_MS,
    ...options,
  });
}

// One group per call (variables: { eventId, inventoryIds, expectedListPrice },
// so each request carries the event it was planned for) → the response body.
// Mutations are never retried. Settled either way, the event's pricing is
// only marked stale (refetchType 'none', as useRunLionAutoGroup does): a
// re-read mid-run would pull merged groups (and their Undo) out from under the
// modal. Compare refetches pricing and the plan when the modal closes.
export function useMergeListings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ eventId, inventoryIds, expectedListPrice }) =>
      (await mergeListings(eventId, inventoryIds, expectedListPrice)).data,
    onSettled: (_data, _err, vars) => {
      queryClient.invalidateQueries({ queryKey: eventPricingKeys.detail(vars.eventId), refetchType: 'none' });
    },
  });
}

// variables: the merge id (not a listing id) → { restoredIds, warnings }.
// Same mark-stale-only rule as useMergeListings.
export function useUndoListingMerge(eventId) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (mergeId) => (await undoListingMerge(mergeId)).data,
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: eventPricingKeys.detail(eventId), refetchType: 'none' });
    },
  });
}
